import React, { useState, useEffect, useRef } from 'react';
import { Save, Upload, Play, FastForward, Book, Moon } from 'lucide-react';
import { fonts } from '../fontMap'
import gsap from 'gsap';
import { getGameData, makeChoice } from '../logic/gameLogic';

function Story({ app, chapterId, onLeaveStory }) {
  const [gameData, setGameData] = useState(getGameData(chapterId));
  const [displayedText, setDisplayedText] = useState("");
  const [isTextComplete, setIsTextComplete] = useState(false);

  const hudRef = useRef(null);
  const backgroundRef = useRef(null);
  const leftCharacterRef = useRef(null);
  const rightCharacterRef = useRef(null);
  const dialogBoxRef = useRef(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    let index = 0;
    setIsTextComplete(false);
    intervalRef.current = setInterval(() => {
      if (index < gameData.dialog.text.length) {
        setDisplayedText(gameData.dialog.text.slice(0, index + 1));
        index++;
      } else {
        clearInterval(intervalRef.current);
        setIsTextComplete(true);
      }
    }, 20);

    return () => {
      clearInterval(intervalRef.current);
    };
  }, [gameData.dialog.text]);

  useEffect(() => {
    gsap.from(backgroundRef.current, {
      duration: 1,
      opacity: 0,
      ease: "power2.inOut"
    });

    if (leftCharacterRef.current && gameData.characters.left?.shouldAnimate) {
      gsap.from(leftCharacterRef.current, {
        duration: 0.8,
        x: -100,
        opacity: 0,
        ease: "power2.out"
      });
    }

    if (rightCharacterRef.current && gameData.characters.right?.shouldAnimate) {
      gsap.from(rightCharacterRef.current, {
        duration: 0.8,
        x: 100,
        opacity: 0,
        ease: "power2.out"
      });
    }

    gsap.from(dialogBoxRef.current, {
      duration: 0.5,
      scaleY: 0,
      transformOrigin: "bottom",
      opacity: 0,
      ease: "power2.out"
    });

    const buttons = document.querySelectorAll('button');
    buttons.forEach(button => {
      button.addEventListener('mouseenter', () => {
        gsap.to(button, {
          duration: 0.3,
          backgroundColor: 'rgba(64, 126, 201, 0.5)',
          ease: 'power2.out'
        });
      });

      button.addEventListener('mouseleave', () => {
        gsap.to(button, {
          duration: 0.3,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          ease: 'power2.out'
        });
      });
    });
  }, []);

  useEffect(() => {
    if (isTextComplete) {
      gsap.fromTo(".choice-button:nth-child(1)",
        { x: -100, opacity: 0 },
        { x: 0, opacity: 1, duration: 0.5, delay: 0 }
      );
      gsap.fromTo(".choice-button:nth-child(2)",
        { x: -100, opacity: 0 },
        { x: 0, opacity: 1, duration: 0.5, delay: 0.2 }
      );
      gsap.fromTo(".choice-button:nth-child(3)",
        { x: -100, opacity: 0 },
        { x: 0, opacity: 1, duration: 0.5, delay: 0.4 }
      );
    }
  }, [isTextComplete]);

  const handleChoice = (choice) => {
    const nextGameData = makeChoice(app, chapterId, gameData.sceneId, choice, onLeaveStory);
    if (nextGameData) {
      setGameData(nextGameData);
    }
  };

  const handleAdvanceDialog = () => {
    if (!isTextComplete) {
      clearInterval(intervalRef.current);
      setDisplayedText(gameData.dialog.text);
      setIsTextComplete(true);
      return;
    }

    if (isTextComplete && !gameData.dialog.choices?.length && gameData.nextDialog) {
      setGameData({
        ...gameData,
        dialog: gameData.nextDialog,
        nextDialog: null
      });
    }
  };

  return (
    <div className="bg-black h-full w-full mx-auto flex flex-col text-white relative overflow-hidden" onClick={handleAdvanceDialog}>
      {gameData.info && (
        <div ref={hudRef} className="bg-gradient-to-b from-black via-black/50 to-transparent p-4 flex flex-col gap-2.5">
          <div className="flex items-center gap-4">
            <div className="text-[#7bb2ea] text-lg drop-shadow-[0_0_5px_#1e3a8a] flex items-center" style={{ fontFamily: fonts.ui }}>{gameData.info.barName}</div>
            <div className="w-[200px] h-2 bg-[#1e3a8a] rounded-sm">
              <div className="h-full rounded-sm bg-gradient-to-r from-[#3b82f6] to-[#60a5fa]" style={{ width: `${gameData.info.barFill}%` }}></div>
            </div>
            <div className="text-[#7bb2ea] text-lg drop-shadow-[0_0_5px_#1e3a8a] flex items-center gap-1.5 ml-4" style={{ fontFamily: fonts.ui }}>
              <Moon size={16} /> {gameData.info.timeName}
            </div>
          </div>
        </div>
      )}

      <div className="relative h-[70%] overflow-hidden">
        <img
          ref={backgroundRef}
          src={gameData.backgroundImage}
          alt="Game Background"
          className="w-full h-full object-cover"
        />
        {gameData.characters.left && (
          <img
            ref={leftCharacterRef}
            src={gameData.characters.left.image}
            alt="Left Character"
            className="absolute bottom-[-10%] left-[-15%] h-[90%] z-10"
          />
        )}
        {gameData.characters.right && (
          <img
            ref={rightCharacterRef}
            src={gameData.characters.right.image}
            alt="Right Character"
            className="absolute bottom-[-10%] right-[-15%] h-[90%] z-10"
          />
        )}
      </div>

      {isTextComplete && gameData.dialog.choices && gameData.dialog.choices.length > 0 && (
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col gap-2.5 w-[80%] max-w-[600px] z-30">
          {gameData.dialog.choices.map((choice, index) => (
            <button
              key={index}
              className="choice-button bg-black border border-black text-white p-3 cursor-pointer drop-shadow-[0_0_5px_#1e3a8a] opacity-0 text-xl"
              onClick={() => handleChoice(choice)}
              style={{ fontFamily: fonts.ui }}
            >
              {choice.text}
            </button>
          ))}
        </div>
      )}

      <div ref={dialogBoxRef} className="bg-gradient-to-b from-black/90 via-black/95 to-transparent p-5 m-2.5 rounded shadow-[0_0_15px_rgba(30,58,138,0.2)] absolute bottom-[15%] left-2.5 right-2.5 min-h-[20%] max-h-[30%] z-20 origin-bottom overflow-y-auto">
        <div className="text-[#60a5fa] font-bold mb-2.5 drop-shadow-[0_0_5px_#1e3a8a] text-2xl" style={{ fontFamily: fonts.title }}>{gameData.dialog.name}</div>
        <div className="text-[#d3d3d3] leading-relaxed mb-5 drop-shadow-[0_0_2px_#fff] text-xl" style={{ fontFamily: fonts.dialog }}>
          "{displayedText}"
        </div>
        {isTextComplete && !gameData.dialog.choices?.length && gameData.nextDialog && (
          <div className="text-[#7bb2ea] text-sm animate-pulse text-center mt-4">
            Click anywhere to continue...
          </div>
        )}
      </div>

      <div className="flex justify-center gap-4 absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black via-black/50 to-transparent">
        <button className="bg-black/70 border border-[#1e3a8a] text-[#7bb2ea] px-4 py-1.5 cursor-pointer flex items-center gap-1.5" style={{ fontFamily: fonts.ui }}>
          <Save size={16} />
        </button>
        <button className="bg-black/70 border border-[#1e3a8a] text-[#7bb2ea] px-4 py-1.5 cursor-pointer flex items-center gap-1.5" style={{ fontFamily: fonts.ui }}>
          <Upload size={16} />
        </button>
        <button className="bg-black/70 border border-[#1e3a8a] text-[#7bb2ea] px-4 py-1.5 cursor-pointer flex items-center gap-1.5" style={{ fontFamily: fonts.ui }}>
          <Play size={16} />
        </button>
        <button className="bg-black/70 border border-[#1e3a8a] text-[#7bb2ea] px-4 py-1.5 cursor-pointer flex items-center gap-1.5" style={{ fontFamily: fonts.ui }}>
          <FastForward size={16} />
        </button>
        <button
            className="bg-black/70 border border-[#1e3a8a] text-[#7bb2ea] px-4 py-1.5 cursor-pointer flex items-center gap-1.5"
            style={{ fontFamily: fonts.ui }}
            onClick={() => app.goToMenu()}
        >
          <Book size={16} />
        </button>
      </div>
    </div>
  );
}

export default Story;
